import { BaseSaver } from './BaseSaver';
import { Data, PlainObject } from './types';

type ToPlainObject<T> = (data: Partial<T>) => PlainObject;
type SetUserData<T> = (patch: PlainObject) => Promise<void>;

type GameBackendSaverProps<T> = {
  toPlainObject: ToPlainObject<T>;
  setUserData: SetUserData<T>;
}

export class GameBackendSaver<T extends Data> extends BaseSaver<T> {
  protected _cache: object;
  protected _throttleMS: number = 1000 * 20;
  protected _throttlePromise: Promise<void>;
  private _saveQueued: boolean = false;
  private readonly toPlainObject: ToPlainObject<T>;
  private setUserData: SetUserData<T>;

  constructor(props: GameBackendSaverProps<T>) {
    super();

    this.toPlainObject = props.toPlainObject;
    this.setUserData = props.setUserData;
  }

  public get enabled(): boolean {
    return true;
  }

  /**
   * @deprecated Do not use
   * @param keys
   */
  public load(keys?: string[]): Promise<T> {
    return Promise.resolve(undefined);
  }

  public async save(data: Partial<T>): Promise<void> {
    this.updateCache(data);

    if (this._saveQueued) {
      this.logger.debugLog('Save already queued; throttling...');
      return this.throttle();
    } else {
      this._saveQueued = true;
      this.logger.debugLog('Save queued; throttling...');
      await this.throttle();
      this._saveQueued = false;
    }

    const cacheCopy: PlainObject = this.toPlainObject(JSON.parse(JSON.stringify(this._cache)));
    this._cache = {};

    return this.setUserData(cacheCopy)
      .then(() => {
        this.logger.debugLog('Successfully saved.');
      })
      .catch((err: Error) => {
        // actually, we don't care
        // throw err;
      });
  }

  /**
   * @deprecated Do not use
   */
  clear(): Promise<void> {
    return Promise.resolve();
  }

  get saveDataKey(): string {
    return '';
  }

  protected throttle(): Promise<void> {
    // tslint:disable-next-line:no-promise-as-boolean
    if (this._throttlePromise) return this._throttlePromise;

    return this._throttlePromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        this._throttlePromise = null;
        resolve();
      }, this._throttleMS);
    });
  }

  public static toPlainObject(data: {
    [key: string]: string | number | null | undefined | boolean;
  }): PlainObject {
    const plainObject: PlainObject = {};

    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) {
        console.warn('Attempting to plain object with entry value undefined');
        return;
      }

      if (value === null) {
        plainObject[key] = value;
      } else if (
        typeof value === 'boolean' ||
        typeof value === 'number' ||
        typeof value === 'string'
      ) {
        plainObject[key] = String(value);
      } else {
        console.warn('Unexpected value type');
      }
    });

    return plainObject;
  }
}
